import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';

const FACTS = {
  baseline: 'r1_conservation_ndvi_baseline',
  latest: 'r1_conservation_ndvi_i',
  change: 'r1_conservation_ndvi_change_percentage',
} as const;

export const StatsTile = () => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year');
  const baselineYear = useFact<number>('r1_conservation_ndvi_baseline_year');
  const projectBaseline = useFact<number>(FACTS.baseline);
  const projectAverage = useFact<number>(FACTS.latest);
  const changePctFromBaseline = useFact<number>(FACTS.change);

  const trendLabel = useGetTrendLabel({
    trendChangeFact: FACTS.change,
  });

  const customExplainerText = useFact<string>('r1_conservation_ndvi_historic_explainer');

  const displayValue = `${getDisplayNumber(changePctFromBaseline?.value, undefined, {
    signDisplay: 'always',
  })} ${t('global.ui.unit.%')}`;

  let fallbackExplainerText =
    changePctFromBaseline?.value > 0
      ? t('shared.projects.project.conservation.explainers.ndvi.trendLabel.increasing', {
          value: displayValue,
        })
      : t('shared.projects.project.conservation.explainers.ndvi.trendLabel.decreasing', {
          value: displayValue,
        });

  if (changePctFromBaseline?.value === 0) {
    fallbackExplainerText = ''; // TODO: science has to decide what "stable" means for NDVI
  }

  return (
    <Stack spacing={10} className='px-6' data-testid='ndvi-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.statsHeading')}
          popover={{
            title: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.vegetation.title'),
            body: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.vegetation.body'),
          }}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.baselineValue.title')}
            fact={projectBaseline}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.baselineValue.title'),
              body: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.baselineValue.body', {
                year: baselineYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'ndvi-baseline-value-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.averageValue.title', {
              year: currentYear.value,
            })}
            fact={projectAverage}
            trend={<FactCapsuleTrend trend={changePctFromBaseline} />}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.averageValue.title', {
                year: currentYear.value,
              }),
              body: t('shared.ncaDetail.details.landMonitoring.ndvi.evolution.explainers.averageValue.body', {
                year: currentYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'ndvi-latest-analysis-popover',
              },
            }}
          />
        </Stack>
      </Stack>
      <ConservationExplainerWell
        benchmark={null}
        trendLabel={trendLabel}
        title={t('global.whatDoesThisMean')}
        body={
          customExplainerText?.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(customExplainerText.value),
              }}
            />
          ) : (
            <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
          )
        }
      />
    </Stack>
  );
};
